import React from 'react';
import { type CalibreRequest, type CalibreFormErrors } from '../types/especie';
import { EspecieValidation } from '../services/especieValidation';
import { EspecieService } from '../services/especieService';

interface UseCalibreFormReturn {
  formData: CalibreRequest;
  errors: CalibreFormErrors;
  loading: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const useCalibreForm = (
  calibreId: number | null,
  idPresentacion: number,
  onSave: (calibre: CalibreRequest) => void
): UseCalibreFormReturn => {
  const [formData, setFormData] = React.useState<CalibreRequest>({
    idPresentacion,
    descripcion: '',
  });
  const [errors, setErrors] = React.useState<CalibreFormErrors>({});
  const [loading, setLoading] = React.useState(false);

  const isValid = React.useMemo(() => {
    return EspecieValidation.isValidCalibre(formData);
  }, [formData]);

  React.useEffect(() => {
    const loadCalibre = async () => {
      if (calibreId) {
        setLoading(true);
        try {
          const calibre = await EspecieService.getCalibreById(calibreId);
          setFormData({
            idCalibre: calibre.idCalibre,
            idPresentacion: calibre.idPresentacion,
            descripcion: calibre.descripcion,
          });
        } catch (error) {
          console.error('Error loading calibre:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadCalibre();
  }, [calibreId]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (errors[name as keyof CalibreFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = EspecieValidation.validateCalibre(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await onSave(formData);
      } catch (error) {
        console.error('Error saving calibre:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = React.useCallback(() => {
    setFormData({
      idPresentacion,
      descripcion: '',
    });
    setErrors({});
  }, [idPresentacion]);

  return {
    formData,
    errors,
    loading,
    isValid,
    handleChange,
    handleSubmit,
    resetForm,
  };
};